import type { ReactNode } from 'react';
import React from 'react';

import type { ErrorTextProps } from '../ErrorText';
import { ErrorText } from '../ErrorText';
import type { GenericMessageDescriptor } from '../GenericFormattedMessage';
import { useFormatMessageGeneric } from '../GenericFormattedMessage';
import { IconType } from '../Icon';
import { StyleUtils } from '../StyledWrappers';
import type { AlertProps } from './Alert';
import { Alert } from './Alert';

export interface ErrorAlertProps extends Pick<AlertProps, 'variant' | 'href'> {
  /** the error to display */
  error?: ErrorTextProps['error'];
  /** the errors to display */
  errors?: ErrorTextProps['error'][];
  /** the title of the error alert */
  title?: GenericMessageDescriptor;
  /** Icon to display */
  icon?: IconType | ReactNode;
  /** Maximum length to show error message before truncating */
  maxErrorLengthTruncated?: number;
  /** should we remove the default margin beneath the alert */
  noMarginBelow?: boolean;
}

/**
 * ErrorAlert
 */
export const ErrorAlert: React.FC<React.PropsWithChildren<ErrorAlertProps>> = ({
  error,
  errors,
  children,
  variant = 'danger',
  title,
  icon = IconType.ExclamationCircle,
  href,
  maxErrorLengthTruncated,
  noMarginBelow,
}) => {
  const { formatMessageGeneric } = useFormatMessageGeneric();

  return (
    <Alert
      variant={variant}
      style={{
        ...StyleUtils.Flex.Row.CenterVertical,
        ...(noMarginBelow ? { margin: 0 } : {}),
      }}
      icon={icon}
      href={href}
    >
      {title && (
        <div style={{ color: 'inherit', fontWeight: 600 }}>
          {formatMessageGeneric(title)}
        </div>
      )}
      {errors ? (
        <ul>
          {errors.map((err, i) => (
            <li key={i}>
              <ErrorText
                error={err}
                hasMargin={false}
                isBold={false}
                inheritColor
                shouldTruncate
              />
            </li>
          ))}
        </ul>
      ) : (
        <ErrorText
          error={error}
          hasMargin={false}
          isBold={false}
          inheritColor
          maxErrorLengthTruncated={maxErrorLengthTruncated}
          shouldTruncate
        >
          {children}
        </ErrorText>
      )}
    </Alert>
  );
};
